import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { FaPencilAlt, FaSave } from 'react-icons/fa';
import { FcDeleteRow } from 'react-icons/fc';
import { Loader } from '../../components/Loader/Loader';
import { CourierAreaFormatted, CourierObj } from '../../fetch/fetchInbound';
import { fetchEditCourierAreaByCourierId } from '../../fetch/fetchEditCourierAreaByCourierId';
import { useGetCourierAreaInfo } from '../../hooks/useGetInboundInfo';
import { ButtonComponent } from '../../ReusableComponents/Button/ButtonComponent';
import analyticsWrapper from '../../utils/analyticsWrapper';
import { EditCourierArea } from './EditCourierArea';
import 'pages/inbound/ShowInboundData.scss';
import { capitalize } from '../../utils/strings';

export function ShowCourierAreaData(): JSX.Element {
  return (
    <div className={'posistionDataTable'}>
      <ShowCourierAreaDataTable />
    </div>
  );
}

const useStyles = makeStyles({
  table: {
    minWidth: 300,
    fontFamily: 'Roboto',
  },

  tableRow: {
    color: 'white',
  },

  notesCellTop: {
    color: '#8D9091',
    fontSize: '16px',
    fontWeight: 900,
    width: '55%',
  },

  notesTextArea: {
    width: '100%',
  },

  cellTop: {
    color: '#8D9091',
    fontSize: '16px',
    fontWeight: 900,
    width: '20%',
  },

  editButtonCellTop: {
    color: '#8D9091',
    fontSize: '16px',
    fontWeight: 900,
    width: '5%',
  },
});

export function ShowCourierAreaDataTable(): JSX.Element {
  const { inboundInfoList, isLoading } = useGetCourierAreaInfo();

  const [activeList, setActiveList] =
    useState<CourierAreaFormatted[]>(inboundInfoList);
  useEffect(() => {
    setActiveList(inboundInfoList);
  }, [inboundInfoList]);

  const tableStyle = useStyles();

  function updateActiveList(
    newItem: CourierAreaFormatted,
    indexOfNewItem,
    type: string
  ): void {
    if (type === 'REPLACE') {
      const NewList: CourierAreaFormatted[] = activeList.map((row, i) => {
        if (i === indexOfNewItem) {
          return newItem;
        } else {
          return row;
        }
      });
      setActiveList(NewList);
    } else if (type === 'DELETE') {
      const NewList: CourierAreaFormatted[] = activeList.filter((row, i) => {
        if (i === indexOfNewItem) {
          return false;
        } else {
          return true;
        }
      });
      setActiveList(NewList);
    }
  }

  return (
    <>
      {isLoading && <Loader />}
      {!isLoading && (
        <>
          <TableContainer component="div">
            <Table className={tableStyle.table} size={'medium'} component="div">
              <TableHead component="div">
                <TableRow className={tableStyle.tableRow} component="div">
                  <TableCell className={tableStyle.cellTop} component="div">
                    Name
                  </TableCell>
                  <TableCell className={tableStyle.cellTop} component="div">
                    Preferred region
                  </TableCell>
                  <TableCell
                    className={tableStyle.notesCellTop}
                    component="div"
                  >
                    Notes
                  </TableCell>
                  <TableCell className={tableStyle.cellTop} component="div">
                    Edit
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody component="div">
                {activeList &&
                  activeList.map((infoRow: CourierAreaFormatted, i: number) => (
                    <RowToShow
                      key={i}
                      infoRow={infoRow}
                      i={i}
                      updateActiveList={updateActiveList}
                    />
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </>
  );
}

function RowToShow({
  infoRow,
  i,
  updateActiveList,
}: {
  infoRow: CourierAreaFormatted;
  i: number;
  updateActiveList: (
    newItem: CourierAreaFormatted,
    indexOfNewItem: number,
    type: string
  ) => void;
}): JSX.Element {
  const [showEditConfigModal, setShowEditConfigModal] =
    useState<boolean>(false);
  if (showEditConfigModal) {
    return (
      <EditDataRow
        infoRow={infoRow}
        mode={'EDIT'}
        i={i}
        updateActiveList={updateActiveList}
        closeEditMode={() => setShowEditConfigModal(false)}
      />
    );
  } else {
    return (
      <DisplayRow
        infoRow={infoRow}
        i={i}
        setShowEditConfigModal={setShowEditConfigModal}
      />
    );
  }
}

function DisplayRow({
  infoRow,
  i,
  setShowEditConfigModal,
}: {
  infoRow: CourierAreaFormatted;
  i: number;
  setShowEditConfigModal: (boolean) => void;
}): JSX.Element {
  return (
    <TableRow key={`${i}`} component="div">
      <TableCell component="div">{courierName(infoRow.courier)}</TableCell>
      <TableCell component="div">
        {(infoRow && infoRow.areaList)
          .map(area => capitalize(area.split('_').join(' ')))
          .join(', ')}
      </TableCell>
      <TableCell component="div">
        {infoRow.notes || infoRow.notes === ''
          ? infoRow.notes
          : infoRow.courier.notes}
      </TableCell>
      <TableCell component="div">
        <ButtonComponent
          variant={'outlined'}
          onClick={() => {
            analyticsWrapper.event({
              category: 'algo-config',
              action: `edit-area-row`,
              label: 'click',
            });
            setShowEditConfigModal(true);
          }}
          startIcon={<FaPencilAlt />}
        >
          {'edit'}
        </ButtonComponent>
      </TableCell>
    </TableRow>
  );
}

export function courierName(courier: CourierObj): string {
  return courier
    ? `${courier.firstName ? courier.firstName : ''} ${
        courier.lastName ? courier.lastName : ''
      }`
    : 'no courier';
}

function EditDataRow({
  infoRow = null,
  mode,
  i,
  updateActiveList,
  closeEditMode,
}: {
  infoRow: CourierAreaFormatted;
  mode: string;
  i: number;
  updateActiveList: (
    newItem: CourierAreaFormatted,
    indexOfNewItem: number,
    type: string
  ) => void;
  closeEditMode: () => void;
}): JSX.Element {
  const [areaList, setAreaList] = useState<string[]>(
    infoRow && infoRow.areaList && infoRow.areaList.length > 0
      ? infoRow.areaList
      : []
  );

  const [notesInputValue, setNotesInputValue] = useState<string>(
    infoRow.notes ? infoRow.notes : infoRow.courier.notes
  );

  const courierObj = infoRow.courier;
  const editTableStyle = useStyles();

  const onChangeNoteState = event => {
    const notesValue = event.target.value;
    setNotesInputValue(notesValue);
  };

  function addAreaListMethod(newAreaList: string[]) {
    setAreaList(newAreaList);
  }

  const updateBackend = async () => {
    const editedNotes: string = notesInputValue ? notesInputValue : '';

    const editedAreaList: string[] =
      areaList && areaList.length > 0 ? areaList.map((obj: string) => obj) : [];

    const infoListResult = await fetchEditCourierAreaByCourierId(
      courierObj.id,
      editedAreaList,
      editedNotes
    );
    if (mode === 'EDIT' && infoListResult) {
      const updatedRow: CourierAreaFormatted = {
        areaList: areaList ? areaList : [],
        courier: courierObj,
        notes: editedNotes,
      };
      updateActiveList(updatedRow, i, 'REPLACE');
      closeEditMode();
    } else if (mode === 'ADD' && infoListResult) {
      const updatedRow: CourierAreaFormatted = {
        areaList: areaList ? areaList : [],
        courier: courierObj,
        notes: editedNotes,
      };
      updateActiveList(updatedRow, i, 'ADD');
      closeEditMode();
    }
  };

  return (
    <>
      <TableRow key={`1xyz`} component="div">
        <TableCell key={`courierlist`} component="div">
          {courierName(infoRow.courier)}
        </TableCell>
        <TableCell key={`shoplist`} component="div">
          <EditCourierArea
            areaList={areaList}
            addAreaListMethod={addAreaListMethod}
          />
        </TableCell>
        <TableCell key={`notes`} component="div">
          <div className="u-pal u-bg-white">
            <textarea
              className={editTableStyle.notesTextArea}
              data-lpignore="true"
              placeholder=""
              name="notes"
              rows={5}
              maxLength={280}
              value={notesInputValue}
              onChange={onChangeNoteState}
            />
          </div>
        </TableCell>
        <TableCell component="div">
          {mode === 'EDIT' && (
            <ButtonComponent
              variant="outlined"
              onClick={async () => {
                analyticsWrapper.event({
                  category: 'algo-config',
                  action: `close-edit-area-row`,
                  label: 'click',
                });
                closeEditMode();
              }}
              startIcon={<FcDeleteRow />}
            >
              {'Back'}
            </ButtonComponent>
          )}
          {mode === 'EDIT' && (
            <ButtonComponent
              variant="outlined"
              onClick={async () => {
                analyticsWrapper.event({
                  category: 'algo-config',
                  action: `save-edit-area-row`,
                  label: 'click',
                });
                await updateBackend();
              }}
              startIcon={<FaSave />}
            >
              {'Save'}
            </ButtonComponent>
          )}
        </TableCell>
      </TableRow>
    </>
  );
}
